import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownToLine,
  LayoutTemplate,
  PhoneIncoming,
  PhoneOutgoing,
  Plus,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";
import { useImportTemplate, useGenerateTemplate, useTemplates } from "@/features/templates/hooks";
import type {
  LibraryTemplateSummary,
  TemplateDirection,
  TemplateDraft,
} from "@/features/templates/api";
import DraftReviewDialog, {
  emptyDraft,
} from "@/features/templates/components/DraftReviewDialog";

export default function TemplatesPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  const canEdit = hasMinRole(role, "manager");

  const q = useTemplates();
  const templates = q.data ?? [];

  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);
  const importMut = useImportTemplate();

  function openDraft(d: TemplateDraft) {
    setDraft(d);
    setReviewOpen(true);
  }

  async function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    try {
      const parsed = await importMut.mutateAsync(file);
      openDraft(parsed);
    } catch (err) {
      const x = err as { response?: { data?: { error?: string } } };
      toast.error(x?.response?.data?.error || "Could not parse that file");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-violet-100 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400">
            <LayoutTemplate className="size-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Templates</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              A library of advanced agent templates — variables, flow and rules
              bundled together. Use one in an agent later.
            </p>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".docx,.md,.txt"
              className="hidden"
              onChange={onFilePicked}
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={importMut.isPending}
            >
              <ArrowDownToLine className="size-4" />
              {importMut.isPending ? "Parsing…" : "Import"}
            </Button>
            <Button variant="outline" onClick={() => setGenerateOpen(true)}>
              <Sparkles className="size-4" />
              Create with AI
            </Button>
            <Button onClick={() => openDraft(emptyDraft())}>
              <Plus className="size-4" />
              New template
            </Button>
          </div>
        )}
      </div>

      {q.isLoading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {!q.isLoading && templates.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <LayoutTemplate className="size-10 text-muted-foreground" />
            <div>
              <p className="text-base font-medium">No templates yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Import a prompt document, generate one with AI, or start from
                scratch.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!q.isLoading && templates.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onClick={() => navigate(`/templates/${t.id}`)}
            />
          ))}
        </div>
      )}

      <DraftReviewDialog
        open={reviewOpen}
        draft={draft}
        onOpenChange={setReviewOpen}
        onCreated={(id) => navigate(`/templates/${id}`)}
      />

      <GenerateDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        onDraft={(d) => {
          setGenerateOpen(false);
          openDraft(d);
        }}
      />
    </div>
  );
}

function TemplateCard({
  template,
  onClick,
}: {
  template: LibraryTemplateSummary;
  onClick: () => void;
}) {
  const Icon = template.direction === "outbound" ? PhoneOutgoing : PhoneIncoming;
  return (
    <button
      onClick={onClick}
      className="flex h-full flex-col rounded-lg border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-muted-foreground" />
          <span className="font-medium">{template.name}</span>
        </div>
        {template.is_platform ? (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
            platform
          </span>
        ) : (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            workspace
          </span>
        )}
      </div>
      {template.description && (
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
          {template.description}
        </p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-3 text-xs text-muted-foreground">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 font-medium capitalize",
            template.direction === "outbound"
              ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
              : "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400",
          )}
        >
          {template.direction}
        </span>
        {template.category && <span>{template.category}</span>}
        <span>· {template.variable_count} fields</span>
      </div>
    </button>
  );
}

function GenerateDialog({
  open,
  onOpenChange,
  onDraft,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDraft: (d: TemplateDraft) => void;
}) {
  const [brief, setBrief] = useState("");
  const [direction, setDirection] = useState<TemplateDirection>("inbound");
  const gen = useGenerateTemplate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!brief.trim()) return;
    try {
      const d = await gen.mutateAsync({ brief: brief.trim(), direction });
      setBrief("");
      onDraft(d);
    } catch (err) {
      const x = err as { response?: { data?: { error?: string } } };
      toast.error(x?.response?.data?.error || "AI generation failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create with AI</DialogTitle>
          <DialogDescription>
            Describe the agent you want. We&apos;ll draft a template with
            fill-in variables for you to review.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Direction</Label>
            <Select
              value={direction}
              onValueChange={(v) => setDirection(v as TemplateDirection)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inbound">Inbound (answers calls)</SelectItem>
                <SelectItem value="outbound">Outbound (places calls)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="brief">Brief</Label>
            <Textarea
              id="brief"
              rows={4}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. A receptionist for a dental clinic that books check-ups, screens for dental emergencies, and takes messages after hours."
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={gen.isPending || !brief.trim()}>
              {gen.isPending ? "Generating…" : "Generate draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
